import React, { useState } from 'react';
import { Check } from 'lucide-react';
// --- HEADLINE FONT CANDIDATES ---
// Each candidate is previewed in context against real Giga headlines so the
// team can judge weight, character, and premium feel side by side. Fonts are
// loaded in index.css. `current` marks the typeface live on the site today.
type FontOption = {
  id: string;
  name: string;
  stack: string;
  weight: number;
  note: string;
  current?: boolean;
};
const FONT_OPTIONS: FontOption[] = [{
  id: 'dm-sans',
  name: 'DM Sans',
  stack: '"DM Sans", Inter, sans-serif',
  weight: 800,
  note: 'What we run today — clean and friendly. Shown at 800 (heavier than the current 700) so you can see the thicker end.',
  current: true
}, {
  id: 'system-ui',
  name: 'System UI (Apple SF Pro)',
  stack: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  weight: 700,
  note: 'The ultimate clean, neutral look. Renders as San Francisco on Apple devices, matching their native aesthetic perfectly.'
}, {
  id: 'inter-display',
  name: 'Inter',
  stack: '"Inter", sans-serif',
  weight: 700,
  note: "Our current body font, but used for headlines. Extremely clean, highly legible, and very close to Apple's San Francisco."
}, {
  id: 'geist',
  name: 'Geist',
  stack: '"Geist", sans-serif',
  weight: 700,
  note: "Vercel's signature font. Highly engineered for modern, clean interfaces with a technical but refined feel."
}, {
  id: 'manrope',
  name: 'Manrope',
  stack: '"Manrope", sans-serif',
  weight: 800,
  note: 'A crossover of geometric and grotesque. Very clean and modern, with excellent legibility at heavy weights.'
}, {
  id: 'public-sans',
  name: 'Public Sans',
  stack: '"Public Sans", sans-serif',
  weight: 800,
  note: 'A strong, neutral typeface. Clean and straightforward without drawing too much attention to itself.'
}, {
  id: 'plus-jakarta',
  name: 'Plus Jakarta Sans',
  stack: '"Plus Jakarta Sans", sans-serif',
  weight: 800,
  note: 'Modern SaaS staple. Slightly more geometric than DM Sans, confident at extrabold without feeling loud.'
}, {
  id: 'bricolage',
  name: 'Bricolage Grotesque',
  stack: '"Bricolage Grotesque", sans-serif',
  weight: 800,
  note: 'Editorial and characterful. Quirky terminals give headlines personality — the most distinctive option.'
}, {
  id: 'archivo',
  name: 'Archivo',
  stack: '"Archivo", sans-serif',
  weight: 800,
  note: 'Tall, slightly condensed grotesk. Reads bold and architectural — great for a punchy, confident hero.'
}, {
  id: 'sora',
  name: 'Sora',
  stack: '"Sora", sans-serif',
  weight: 800,
  note: 'Crisp geometric face. Feels technical and premium, a touch more futuristic than DM Sans.'
}, {
  id: 'hanken',
  name: 'Hanken Grotesk',
  stack: '"Hanken Grotesk", sans-serif',
  weight: 800,
  note: 'Warm, rounded grotesk. Sits closest to DM Sans in feel but with a sturdier, friendlier weight.'
}, {
  id: 'fraunces',
  name: 'Fraunces',
  stack: '"Fraunces", serif',
  weight: 700,
  note: 'A premium serif wildcard. Adds editorial warmth and gravitas if you want headlines to feel less techy.'
}, {
  id: 'familjen',
  name: 'Familjen Grotesk',
  stack: '"Familjen Grotesk", sans-serif',
  weight: 700,
  note: 'Sharp Grotesk look-alike. Rounded grotesque with a single-story "a" — the closest free match to the heavy specimen, warm and friendly at bold.'
}, {
  id: 'space-grotesk',
  name: 'Space Grotesk',
  stack: '"Space Grotesk", sans-serif',
  weight: 700,
  note: 'Geometric grotesque with character in its terminals. More technical and a touch quirkier than the reference, but in the same family of feel.'
}, {
  id: 'schibsted',
  name: 'Schibsted Grotesk',
  stack: '"Schibsted Grotesk", sans-serif',
  weight: 800,
  note: 'Sturdy modern grotesque that goes genuinely heavy (800/900). Confident and wide at extrabold — close to the bold, blocky weight of the specimen.'
}, {
  id: 'onest',
  name: 'Onest',
  stack: '"Onest", sans-serif',
  weight: 800,
  note: 'Clean geometric face with rounded, single-story letterforms. Neutral and premium, sitting between Inter and the rounder Sharp Grotesk look.'
}];
// Real Giga headline content so each face is judged in context, not on
// generic specimen text.
const HERO_LINE = 'The AI coworker that actually does the work.';
const SECTION_LINE = 'Built to do the work — not just talk about it.';
const SHORT_LINE = 'What can Giga do for me?';
function FontSpecimen({
  font
}: {
  font: FontOption;
}) {
  return <div className="rounded-3xl border border-neutral-200/70 bg-white overflow-hidden" data-id="element-5714">
      {/* Header strip */}
      <div className="flex items-center justify-between gap-4 px-6 md:px-8 py-4 border-b border-neutral-100 bg-[#FAF8F4]" data-id="element-5715">
        <div className="flex items-center gap-3" data-id="element-5716">
          <h3 className="text-lg font-bold text-[#2B211A]" data-id="element-5717">{font.name}</h3>
          {font.current && <span className="inline-flex items-center gap-1 rounded-full bg-[#EA580C]/10 text-[#C2410C] text-[11px] font-bold uppercase tracking-wider px-2.5 py-1" data-id="element-5718">
              <Check size={12} strokeWidth={3} data-id="element-5719" />
              Current
            </span>}
        </div>
        <span className="text-xs font-medium text-neutral-400" data-id="element-5720">
          Weight {font.weight}
        </span>
      </div>

      {/* Live specimen on the brand light surface */}
      <div className="px-6 md:px-10 py-8 md:py-10" data-id="element-5721">
        <p className="text-[11px] font-bold uppercase tracking-[3px] text-neutral-400 mb-5" data-id="element-5722">
          Hero
        </p>
        <p className="text-3xl md:text-5xl text-[#2B211A] tracking-tight leading-[1.05] mb-10" style={{
        fontFamily: font.stack,
        fontWeight: font.weight
      }} data-id="element-5723">
          {HERO_LINE}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10" data-id="element-5724">
          <div data-id="element-5725">
            <p className="text-[11px] font-bold uppercase tracking-[3px] text-neutral-400 mb-4" data-id="element-5726">
              Section heading
            </p>
            <p className="text-2xl md:text-3xl text-[#2B211A] tracking-tight leading-[1.1]" style={{
            fontFamily: font.stack,
            fontWeight: font.weight
          }} data-id="element-5727">
              {SECTION_LINE}
            </p>
          </div>
          <div data-id="element-5728">
            <p className="text-[11px] font-bold uppercase tracking-[3px] text-neutral-400 mb-4" data-id="element-5729">
              Short headline
            </p>
            <p className="text-2xl md:text-3xl text-[#2B211A] tracking-tight leading-[1.1]" style={{
            fontFamily: font.stack,
            fontWeight: font.weight
          }} data-id="element-5730">
              {SHORT_LINE}
            </p>
          </div>
        </div>

        {/* On the dark brand surface, with body copy underneath for contrast */}
        <div className="mt-8 rounded-2xl bg-[#2B211A] px-6 md:px-8 py-7" data-id="element-5731">
          <p className="text-[11px] font-bold uppercase tracking-[3px] text-white/40 mb-4" data-id="element-5732">
            On dark
          </p>
          <p className="text-2xl md:text-4xl text-white tracking-tight leading-[1.08] mb-3" style={{
          fontFamily: font.stack,
          fontWeight: font.weight
        }} data-id="element-5733">
            {SHORT_LINE}
          </p>
          <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-xl font-body" data-id="element-5734">
            Body copy stays Inter — only the headline face changes. This shows
            how the display weight pairs with the running text it sits above.
          </p>
        </div>

        <p className="mt-6 text-sm text-neutral-500 leading-relaxed max-w-2xl" data-id="element-5735">
          {font.note}
        </p>
      </div>
    </div>;
}
export function FontOptions() {
  const [headlineText, setHeadlineText] = useState(HERO_LINE);
  return <div className="min-h-screen bg-white pt-32 pb-24" data-id="element-5736">
      <div className="max-w-[1180px] mx-auto px-6 md:px-10" data-id="element-5737">
        <div className="mb-12" data-id="element-5738">
          <p className="text-[11px] font-bold uppercase tracking-[4px] text-[#EA580C] mb-4" data-id="element-5739">
            Internal · Typography
          </p>
          <h1 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight mb-4" data-id="element-5740">
            Headline font options
          </h1>
          <p className="text-lg text-neutral-500 max-w-2xl leading-relaxed" data-id="element-5741">
            Each candidate is a thicker, premium display face shown on real Giga
            headlines — light surface, dark surface, and alongside body copy —
            so you can see exactly how it reads in application. Body text stays
            Inter throughout; only the headline typeface changes.
          </p>

          {/* Type-your-own headline to test any candidate live */}
          <div className="mt-8 max-w-xl" data-id="element-5742">
            <label htmlFor="headline-input" className="block text-[11px] font-bold uppercase tracking-[3px] text-neutral-400 mb-2" data-id="element-5743">
              Try your own headline
            </label>
            <input id="headline-input" type="text" value={headlineText} onChange={e => setHeadlineText(e.target.value)} placeholder="Type a headline to preview…" className="w-full px-4 py-3 rounded-xl bg-[#FAF8F4] border border-neutral-200 text-[#2B211A] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#EA580C]/30 focus:border-[#EA580C]/40 transition" data-id="element-5744" />
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-5745">
              {FONT_OPTIONS.map(font => <div key={`try-${font.id}`} className="rounded-xl border border-neutral-200/70 bg-white px-4 py-3" data-id="element-5746">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1" data-id="element-5747">
                    {font.name}
                  </p>
                  <p className="text-xl text-[#2B211A] tracking-tight leading-snug" style={{
                fontFamily: font.stack,
                fontWeight: font.weight
              }} data-id="element-5748">
                    {headlineText || 'Type a headline to preview…'}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        <div className="space-y-10" data-id="element-5749">
          {FONT_OPTIONS.map(font => <FontSpecimen key={font.id} font={font} data-id="element-5750" />)}
        </div>
      </div>
    </div>;
}